/**
 * @file ExtractedDate.tsx
 *
 * @description A date-time picker component for law enforcement applications
 * that handles automatic time zone conversion between local and UTC formats.
 * This component is designed for police officers to accurately record
 * timestamps in incident reports, evidence collection, and case documentation.
 *
 * @module Components
 * @requires React
 * @requires Material-UI
 */

import React from "react";
import { Box, SxProps } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Enable the UTC and timezone plugins for dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Props for the ExtractedDate component
 * @interface ExtractedDateProps
 * @property {any} date - The date value to display (can be string in ISO format or dayjs object)
 * @property {string} label - The label text for the date picker field
 * @property {boolean} [disabled] - Whether the date picker should be disabled
 * @property {(value: any) => void} handleValueChange - Callback function that receives the updated date value in UTC format
 * @property {SxProps} [sx] - Optional MUI styling props to apply to the container
 */
interface ExtractedDateProps {
  date: string | dayjs.Dayjs | null | undefined;
  label: string;
  dateFormat: string;
  disabled?: boolean;
  handleValueChange: (value: string | null) => void;
  sx?: SxProps;
}

/**
 * @name ExtractedDate
 *
 * @description A date-time picker component with timezone handling for law enforcement applications.
 * The component displays dates in local time but stores and returns them in UTC format
 * to ensure consistency in timestamped records across different jurisdictions.
 *
 * @param {ExtractedDateProps} props - The component props
 *
 * @returns {React.ReactElement} A date-time picker with timezone information
 *
 * @example
 * // Basic usage with required props
 * <ExtractedDate
 *   date="2023-05-15T14:30:00Z"
 *   label="Incident Time"
 *   handleValueChange={(newDate) => setIncidentTime(newDate)}
 * />
 *
 * @example
 * // With additional optional props
 * <ExtractedDate
 *   date={reportData.witnessed_at}
 *   label="Time Witnessed"
 *   disabled={isReadOnly}
 *   handleValueChange={updateWitnessedTime}
 *   sx={{ mt: 2, width: "100%" }}
 * />
 *
 * @returns {JSX.Element} A customized date-picker time component tailored for police use.
 */
export const ExtractedDate: React.FC<ExtractedDateProps> = ({
  date,
  handleValueChange,
  sx,
  disabled,
  label,
  dateFormat,
}) => {
  /**
   * @name parseDate
   *
   * @description Converts various date formats to a dayjs object in local time.
   *
   * @param {string | dayjs.Dayjs | null | undefined} dateValue - The date value to parse (string in ISO format or dayjs object)
   * @returns {dayjs.Dayjs|null} A dayjs object in local time or null if input is falsy
   */
  const parseDate = (dateValue: string | dayjs.Dayjs | null | undefined) => {
    if (!dateValue) return null;

    if (typeof dateValue === "string") {
      return dayjs.utc(dateValue, dateFormat).local();
    }

    return dayjs(dateValue);
  };

  // Convert input date to local time for display
  const displayValue = parseDate(date);
  /**
   * @name getTimezoneOffset
   *
   * @description Calculates the current timezone offset string in the format ±HH:MM.
   * This is used for displaying the local timezone information to the officer.
   *
   * @returns {string} Formatted timezone offset (e.g., "-05:00" for EST)
   */
  const getTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset();
    const hours = Math.abs(Math.floor(offset / 60));
    const minutes = Math.abs(offset % 60);
    const sign = offset > 0 ? "-" : "+";
    return `${sign}${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Box
      sx={{
        ...sx,
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
          ampm={false} // Use 24-hour format for law enforcement standard
          label={label}
          disabled={disabled}
          value={displayValue}
          views={["year", "month", "day", "hours", "minutes", "seconds"]}
          timezone={"default"} // Use the browser's default timezone
          onChange={(newValue) => {
            if (!newValue) {
              // Handle null or undefined value
              handleValueChange(null);
              return;
            }
            // Convert selected local time to UTC format for storage
            // This ensures consistent timestamps regardless of officer's timezone
            const utcValue = newValue.utc().format(dateFormat);
            handleValueChange(utcValue);
          }}
          slotProps={{
            textField: {
              fullWidth: true,
              sx: { mb: 2 },
              // Display helper text showing the exact local time with timezone info
              helperText: `Local Time (UTC${getTimezoneOffset()}): ${
                displayValue ? displayValue.format("YYYY-MM-DD HH:mm:ss[Z]") : ""
              }`,
            },
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};
